'use client';

import config from '@actr/config';
import { IconPlaneArrival } from '@actr/icons/plane-arrival';
import { IconPlaneDeparture } from '@actr/icons/plane-departure';
import { ThemeColorValueImportance } from '@actr/style/src/specs/theme-v2';
import { ButtonTypes } from '@actr/ui/src/components/_atoms/button';
import { Text, TextAlight } from '@actr/ui/src/components/_atoms/text';
import { Price } from '@actr/ui/src/components/price';
import { joinLocationParts } from '@actr/utilities/src/avia/format-location';
import { formatDate, parseDate } from '@actr/utilities/src/date/format';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { SeoRecommendation } from '@/services/models/avia/recommendation';
import SeoButton from '@/ui/seo-button';
import { plural } from '@/utils/string';

import * as Styled from './style.css';

const RECOMMENDATION_DATE_FORMAT = 'd MMM, EEEEEE';

const formatRecommendationDate = (date: string) => {
  const parsedDate = parseDate(date, config.dateTime.serverDateFormatFns);

  return formatDate(parsedDate, RECOMMENDATION_DATE_FORMAT);
};

export type RecommendationProps = {
    recommendation: SeoRecommendation;
    isHidden: boolean;
}

const Recommendation: React.FC<RecommendationProps> = ({ recommendation, isHidden }) => {

  const { arr, dep, suppliers, tags, price, flight_duration, url } = recommendation || {};
  const hasAdditionalParams = Boolean(tags?.transfer_count || tags?.is_lowcost || tags?.is_night_transfer || tags?.is_change_airport);
  const isCompactMobile = !hasAdditionalParams;

  const suppliersArr: string[] = [];
  suppliers.forEach(supplier => suppliersArr.push(supplier?.title));

  function renderSupplierLogo(value: string) {
    const path = `${config.staticURL}/suppliers_redesign/tail/${value}.png`;
    return (
      <div className={Styled.SupplierLogoWrapper}>
        <img
          className={Styled.SupplierLogo}
          alt={`${value[0]}`}
          key={value}
          src={path}
        />
      </div>
    );
  }

  function renderFlightCardMainInfo() {
    return (
      <div className={Styled.FlightLineItems}>
        <IconPlaneDeparture className={Styled.FlightLineIconDeparture} />
        <div className={Styled.LineWrapper}>
          <div className={Styled.FlightLineItem}>
            <div className={Styled.FlexWrapper}>
              <Text
                className={Styled.AirportCodeText}
                color={ThemeColorValueImportance.LIGHTER}
                font="paragraph.xs"
                align={TextAlight.LEFT}>
                {dep.airport.code}
              </Text>
              <Text
                className={Styled.AirportCodeText}
                color={ThemeColorValueImportance.LIGHTER}
                font="paragraph.xs"
                align={TextAlight.RIGHT}>
                {arr.airport.code}
              </Text>
            </div>
            <div className={Styled.FlightLine} />
            {!isCompactMobile && (
              <div className={Styled.TagsWrapperDesktop}>
                {renderFlightInfoTags()}
              </div>
            )}
          </div>
        </div>
        <IconPlaneArrival className={Styled.FlightLineIconArrival} />
      </div>
    );
  }

  function renderFlightInfoTags() {
    return (
      <>
        {tags?.transfer_count && (
        <div className={Styled.Tag()}>
          <Text className={Styled.TagText} font="paragraph.xs">
            {tags.transfer_count}
            {' '}
            {plural('пересадка', 'пересадки', 'пересадок', tags.transfer_count)}
          </Text>
        </div>
        )}
        {tags?.is_lowcost && (
        <div className={Styled.ItemTag()}>
          <Text className={Styled.TagText} font="paragraph.xs">Лоукост</Text>
        </div>
        )}
        {tags?.is_night_transfer && (
        <div className={Styled.ItemTag()}>
          <Text className={Styled.TagText} font="paragraph.xs">Ночная пересадка</Text>
        </div>
        )}
        {tags?.is_change_airport && (
        <div className={Styled.ItemTag({ isChangeAirport: true })}>
          <Text className={Styled.TagText} font="paragraph.xs">Смена аэропорта</Text>
        </div>
        )}
      </>
    );
  }

  function renderDepArrInfo(city: string, airport: string, time: string, date: string, code: string, isArr: boolean = false) {
    return (
      <div className={Styled.DepArrInfoWrapp({ isArr, isCompactMobile })}>
        <div className={Styled.DepArrTimeDateRow}>
          <Text className={classNames(Styled.DepArrText({ isArr }), Styled.DepArrTime)} font="paragraph.xl.semi">{time}</Text>
          <Text
            className={classNames(Styled.DepArrText({ isArr }), Styled.DepArrDate({ isCompactMobile }))}
            color={ThemeColorValueImportance.LIGHTER}>
            {formatRecommendationDate(date)}
          </Text>
        </div>
        <Text className={Styled.DepArrAirportText({ isArr })} color={ThemeColorValueImportance.LIGHTER} font="paragraph.xs">
          {joinLocationParts(city, airport, code)}
        </Text>
      </div>
    );
  }

  return (
    <div className={Styled.RecommendationWrapper({ isHidden })}>
      <div className={Styled.MainInfo}>
        <div className={Styled.CardHeaderWrapper}>
          <Text className={Styled.SuppliersText} font="paragraph.s.semi">{suppliersArr.join(', ')}</Text>
          <Text color={ThemeColorValueImportance.LIGHTER} font="paragraph.s" align={TextAlight.RIGHT}>
            В пути
            {' '}
            {flight_duration}
          </Text>
        </div>
        <div className={Styled.FlightInfoWrapper}>
          {renderSupplierLogo(suppliers[0].code)}
          {renderDepArrInfo(dep.city.title, dep.airport.title, dep.time, dep.date, dep.airport.code)}
          {renderFlightCardMainInfo()}
          {!isCompactMobile && (
            <div className={Styled.TagsWrapperMobile}>
              {renderFlightInfoTags()}
            </div>
          )}
          {renderDepArrInfo(arr.city.title, arr.airport.title, arr.time, arr.date, arr.airport.code, true)}
        </div>
      </div>
      <div className={Styled.CardFooterInfo}>
        <Price font="title.x2l" prefix="от" amount={price} />
        <SeoButton
          className={Styled.RecommendationButton}
          url={url}
          buttonType={ButtonTypes.PRIMARY}>
          Уточнить цену
        </SeoButton>
      </div>
    </div>
  );
};

export default observer(Recommendation);
